import React, { useContext, useEffect, useRef, useState } from "react";
import { CategoryContext } from "./CategoryContext";
import CategoryList from "./CategoryList";
import AddEditViewCategoryType from "./AddEditViewCategoryType";
import AddEditViewCategory from "./AddEditViewCategory";
import { AppContext } from "../../../Context/AppContext";
import AssociatedAttributes from "./components/AssociatedAttributes";
import AssociatedRules from "./components/AssociatedRules";
import RelevantCategories from "./components/RelevantCategories";
import PdwAttributeOrder from "./components/PdwAttributeOrder";
import AbsAttributeOrder from "./components/AbsAttributeOrder";
import CategoryAttributeConstraint from "./components/CategoryAttributeConstraint";
import SkeletonElement from "./Shimmer/SkeletonElement";
import { toast } from "Components/DesignSystem/Toast/useToast";
import VariantDefiningAttributes from "./components/VariantDefiningAttributes";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Button from "Components/DesignSystem/Button/Button";
import Menu from "Components/DesignSystem/Menu/Menu";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import { useDismissable } from "Components/DesignSystem/useDismissable";
import { ABS_ATTRIBUTES, ACCORDION_FLUSH_ABS, ACCORDION_FLUSH_CF, ACCORDION_FLUSH_COLLAPSE_ABS, ACCORDION_FLUSH_COLLAPSE_CF, CF_ATTRIBUTES } from "./CategoryConstants";

function CategoryTypeManage(props) {
  const appData = useContext(AppContext);
  const industryId =
    appData.get_selected_industry() && appData.get_selected_industry().id;
  let { editingItem, setEditingItem, deleteinfo } = useContext(CategoryContext);
  let [categoryName, setCategoryName] = useState("");
  let [categoryTypeName, setCategoryTypeName] = useState("");
  let [apiCallDone, setApiCallDone] = useState(false);
  let [categoryDetailPageLoaded, setCategoryDetailPageLoaded] = useState(false);
  const [showShimmer, setShowShimmer] = useState(true);

  const [reRender, setReRender] = useState(true);

  /* The empty state's Create menu (was a Bootstrap dropdown). DS `Menu` is
     presentational — this pair owns whether it is mounted, and `useDismissable`
     supplies Escape/outside-click, which the plugin used to. */
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);
  useDismissable({
    isOpen: createMenuOpen,
    onDismiss: () => setCreateMenuOpen(false),
    isInside: (target) => !!createMenuRef.current?.contains(target),
  });

  useEffect(() => {
    if (editingItem && editingItem.id !== 0) setReRender(false);
    setTimeout(() => {
      setReRender(true);
    }, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem && editingItem.id]);

  useEffect(() => {
    setEditingItem(null);

    if (
      props.props &&
      props.props.match.params &&
      props.props.match.params.type
    ) {
      if (props.props.match.params.type === "ct") {
        if (props.props.match.params.uid) {
          setEditingItem({
            type: "ct",
            form: "view",
            id: props.props.match.params.uid,
          });
        } else {
          setEditingItem({
            type: "ct",
            form: "edit",
            id: "0",
          });
        }
      }
      if (props.props.match.params.type === "c") {
        if (props.props.match.params.uid) {
          setEditingItem({
            type: "c",
            form: "view",
            id: props.props.match.params.uid,
          });
        } else {
          setEditingItem({
            type: "c",
            form: "edit",
            id: "0",
            category_type_id: undefined,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryId]);

  const categoryContextData = useContext(CategoryContext);

  return (
    <React.Fragment>
      <div id="taxonomy-page" data-testid="categories-page" className="container-fluid p-0">
        <div className="row mt-0 pt-0 ">
          <div className="col-md-3">
            {/* The § 99 status-strip grammar: spinner inline and aria-hidden — the
                Alert is the live region, the message carries the announcement.
                Info/success are polite `role="status"`; only Error is `role="alert"`. */}
            {deleteinfo.status === "Loading" && (
              <Alert tone="info" live data-testid="category-delete-status-alert">
                <span className="tw-inline-flex tw-items-center tw-gap-2">
                  <Spinner size="sm" aria-hidden="true" data-testid="category-delete-status-spinner" />
                  <span>{deleteinfo.message}</span>
                </span>
              </Alert>
            )}
            {deleteinfo.status === "Success" && (
              <Alert tone="success" live data-testid="category-delete-status-alert">
                {deleteinfo.message}
              </Alert>
            )}
            {deleteinfo.status === "Error" && (
              <Alert tone="danger" live data-testid="category-delete-status-alert">
                {deleteinfo.message}
              </Alert>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap">
          <div className="col-sm-12 col-md-12 col-lg-2 relative md:h-page-content p-0 top-0 tw-border-r-2 tw-border-edge">
            <CategoryList
              props={props}
              setApiCallDone={setApiCallDone}
              setCategoryDetailPageLoaded={setCategoryDetailPageLoaded}
            />
          </div>

          {editingItem === null && apiCallDone && (
            <div className="col-sm-12 col-md-12 col-lg-10 ps-0 pe-0">
              <EmptyState
                size="md"
                className="mt-5"
                data-testid="categories-empty-state"
                illustration={<NoDataIllustration />}
                title="No data found"
                description="You can create category for the products."
                action={
                  <div
                    ref={createMenuRef}
                    id="taxonomy-create-category-menu"
                    className="tw-relative tw-inline-block"
                  >
                    <Button
                      variant="secondary"
                      id="taxonomy-btn-create-category"
                      data-testid="categories-create-btn"
                      aria-haspopup="menu"
                      aria-expanded={createMenuOpen}
                      onClick={() => setCreateMenuOpen((open) => !open)}
                    >
                      Create Category
                    </Button>
                    {createMenuOpen && (
                      <div className="tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-top-full tw-mt-1 tw-z-50 tw-w-44">
                        <Menu
                          variant="compact"
                          data-testid="categories-create-menu"
                          items={[
                            {
                              id: "category-type",
                              label: "Category Type",
                              "data-testid": "categories-create-category-type-btn",
                              onClick: () => {
                                setCreateMenuOpen(false);
                                if (
                                  appData.pm.checkPermission("gmscategory_create")
                                ) {
                                  categoryContextData.setEditingItem({
                                    type: "ct",
                                    form: "edit",
                                    id: "0",
                                  });
                                } else {
                                  /* toast.success for a denial is the legacy tone,
                                     preserved on purpose — the sibling item below uses
                                     info. Same quirk, same place, until a decision. */
                                  toast.success(
                                    "You don't have permission to create the category type"
                                  );
                                }
                              },
                            },
                            {
                              id: "category",
                              label: "Category",
                              "data-testid": "categories-create-category-btn",
                              onClick: () => {
                                setCreateMenuOpen(false);
                                if (
                                  appData.pm.checkPermission("gmscategory_create")
                                ) {
                                  categoryContextData.setEditingItem({
                                    type: "c",
                                    form: "edit",
                                    id: "0",
                                    category_type_id: undefined,
                                  });
                                } else {
                                  toast.info(
                                    "You don't have permission to create the category"
                                  );
                                }
                              },
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                }
              />
            </div>
          )}
          <div className="col-sm-12 col-md-12 col-lg-10 md:h-page-content w-full p-0">
            <div className="md:overflow-y-auto md:max-h-full">
              {editingItem !== null &&
                editingItem !== undefined &&
                editingItem.type === "ct" && (
                  <>
                    <AddEditViewCategoryType />
                  </>
                )}

              {editingItem !== null &&
                editingItem !== undefined &&
                editingItem.type === "c" && (
                  <>
                    <AddEditViewCategory
                      categoryName={categoryName}
                      setCategoryName={setCategoryName}
                      categoryTypeName={categoryTypeName}
                      setCategoryTypeName={setCategoryTypeName}
                      setCategoryDetailPageLoaded={setCategoryDetailPageLoaded}
                      showShimmer={showShimmer}
                      setShowShimmer={setShowShimmer}
                    />
                    {editingItem.id !== "0" && reRender && (
                      <div
                        className="ms-2 ps-1"
                        style={{ marginBottom: "50vh" }}
                      >
                        {categoryDetailPageLoaded ? (
                          <div>
                            <AssociatedAttributes />
                            <br />
                            <VariantDefiningAttributes />
                            <br />
                            <AssociatedRules />
                            <br />
                            <RelevantCategories />
                            <br />
                            <PdwAttributeOrder />
                            <br />
                            <AbsAttributeOrder title={ABS_ATTRIBUTES} id={'Abs'} />
                            <br />
                            <CategoryAttributeConstraint />
                            {/* <br />
                            <AbsAttributeOrder title={CF_ATTRIBUTES} id={'CF'} /> */}

                          </div>
                        ) : (
                          <div>
                            {showShimmer && (
                              <div>
                                <br />
                                <SkeletonElement type={"text"} />
                                <br />
                                <SkeletonElement type={"title"} />
                                <br />
                                <SkeletonElement type={"title"} />
                                <br />
                                <SkeletonElement type={"title"} />
                                <br />
                                <SkeletonElement type={"title"} />
                                <br />
                                <SkeletonElement type={"title"} />
                                <br />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CategoryTypeManage;
